import { useEffect, useRef, useState } from 'react';
////////////////////////////////////////////////////////////////////
import { useAnimatedCounter } from '@/app/hooks';
import {
	Categories,
	Coins,
	CoinsAnimation,
	DailyCombo,
} from '@/app/shared/components';
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import { setUploadActivity } from '@/core/redux/slices';
import ReactGA from 'react-ga4';

/**
 * The Build page component.
 *
 * This component renders the Build page, which displays the user's current coin balance,
 * a daily combo reward, and a list of activity categories that the user can browse.
 *
 * The component uses a combination of React hooks and Redux to manage the state of the
 * page. The `useCustomSelector` hook is used to retrieve the current coin balance and the
 * daily combo rewards from the Redux store. The `useCustomDispatch` hook is used to dispatch
 * actions to the Redux store to update the state of the page.
 *
 * The component also uses the `useAnimatedCounter` hook to animate the coin balance when
 * the daily combo reward is collected. The animation is triggered when the user collects
 * the daily combo reward by calling the `handleAnimationCollectedCoins` function.
 *
 * @returns The Build page component.
 */
const Build: React.FC = () => {
	/*********************
	// #region Constants
	**********************/

	/*********************
	// #region Redux
	**********************/
	const { counter } = useCustomSelector((state) => state.counter);
	const { dailyComboRewards, uploadedActivity } = useCustomSelector(
		(state) => state.categories
	);

	/*********************
	// #region Hooks
	**********************/
	const coinsRef = useRef<HTMLDivElement>(null);
	const dispatch = useCustomDispatch();
	const [animationCounterTrigger, _] = useState<boolean>(false);
	const counterAnimation = useAnimatedCounter(
		counter,
		counter + dailyComboRewards,
		1000,
		animationCounterTrigger
	);

	/*********************
	// #region States
	**********************/
	const [animateCoins, setAnimateCoins] = useState<boolean>(false);

	/*********************
	// #region Observables
	**********************/
	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/build',
			title: 'Activities',
		});
	}, []);

	useEffect(() => {
		if (!uploadedActivity) return;

		handleAnimationCollectedCoins();
		dispatch(setUploadActivity(false));
	}, [uploadedActivity]);

	/*********************
	// #region Functions  
	**********************/
	const handleAnimationCollectedCoins = () => {
		setAnimateCoins(true);

		setTimeout(() => {
			setAnimateCoins(false);
		}, 1000);
	};

	/*********************
	// #region Template
	**********************/
	return (
		<div className="flex flex-col gap-2 pb-32 pt-8 mx-4">
			<div ref={coinsRef}>
				<Coins isAnimated={animateCoins} counter={counterAnimation} />
			</div>
			<DailyCombo onCloseClaim={handleAnimationCollectedCoins} />
			<Categories />
			<CoinsAnimation animateCoins={animateCoins} coinsRef={coinsRef} />
		</div>
	);
};

export default Build;
